import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, ShoppingBag, Package, Users, AlertTriangle, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api, { inr } from "@/lib/api";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="py-20 text-center uplabel text-[var(--olive)]">Loading…</div>;

  const stats = [
    { label: "Total Revenue", value: inr(data.revenue), icon: IndianRupee },
    { label: "Total Orders", value: data.total_orders, icon: ShoppingBag },
    { label: "Products", value: data.total_products, icon: Package },
    { label: "Customers", value: data.customers, icon: Users },
  ];

  const lowStock = data.low_stock || [];
  const threshold = data.low_stock_threshold ?? 5;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-4xl text-[var(--bronze)] mb-2">Dashboard</h1>
      <p className="text-[var(--olive)] mb-10">An overview of your store's performance.</p>

      {lowStock.length > 0 && (
        <div data-testid="low-stock-alert" className="mb-10 border border-[var(--maroon)]/40 bg-[#F3EECB]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)]">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-full bg-[var(--maroon)]/10 text-[var(--maroon)]"><AlertTriangle className="w-5 h-5" strokeWidth={1.6} /></span>
              <div>
                <div className="font-serif text-xl text-[var(--bronze)]">Low Stock Warning</div>
                <div className="text-xs text-[var(--olive)]">{lowStock.length} {lowStock.length === 1 ? "piece needs" : "pieces need"} restocking (≤ {threshold} left)</div>
              </div>
            </div>
            <Link to="/admin/products" className="hidden sm:flex items-center gap-1.5 uplabel text-[var(--bronze)] hover:text-[var(--olive)] transition-colors">Manage <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="divide-y divide-[var(--line)]">
            {lowStock.map((p) => {
              const out = p.stock <= 0;
              return (
                <Link key={p.id} to="/admin/products" data-testid={`low-stock-${p.id}`} className="flex items-center gap-4 px-6 py-3 hover:bg-[#FBF7DE] transition-colors">
                  <img src={p.images?.[0]} alt="" className="w-10 aspect-[3/4] object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[var(--bronze)] truncate">{p.name}</div>
                    <div className="text-xs text-[var(--olive)] capitalize">{p.audience} · {String(p.category).replace("-", " ")}</div>
                  </div>
                  <span className={`uplabel text-[0.58rem] px-2.5 py-1 ${out ? "bg-[var(--maroon)] text-[#FBF7DE]" : "bg-[var(--olive)] text-[#FBF7DE]"}`}>
                    {out ? "Out of stock" : `${p.stock} left`}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {stats.map((s) => (
          <div key={s.label} className="border border-[var(--line)] p-6 bg-[#F3EECB]">
            <s.icon className="w-6 h-6 text-[var(--olive)]" strokeWidth={1.3} />
            <div className="font-serif text-3xl text-[var(--bronze)] mt-4">{s.value}</div>
            <div className="uplabel text-[0.6rem] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-[var(--line)] p-6">
          <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Best Sellers</h2>
          {data.best_sellers.length === 0 ? <p className="text-[var(--olive)] text-sm">No sales data yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.best_sellers} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#7A6A44", fontSize: 11 }} />
                <Tooltip cursor={{ fill: "rgba(160,139,92,0.1)" }} contentStyle={{ background: "#FBF7DE", border: "1px solid rgba(160,139,92,0.3)" }} />
                <Bar dataKey="qty" fill="#A08B5C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-[var(--line)] p-6">
          <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Recent Orders</h2>
          <div className="space-y-3">
            {data.recent_orders.length === 0 ? <p className="text-[var(--olive)] text-sm">No orders yet.</p> :
              data.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm border-b border-[var(--line)] pb-3">
                  <div><div className="text-[var(--bronze)]">{o.order_number}</div><div className="text-xs text-[var(--olive)]">{o.payment_method.toUpperCase()} · {o.payment_status}</div></div>
                  <span className="font-medium text-[var(--bronze)]">{inr(o.total)}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
